import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleData } from "@/hooks/useModuleData";
import { usePagedData } from "@/hooks/usePagedData";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Loader } from "@/components/common/Loader";
import { eur, num, shortDate } from "@/lib/format";
import { openPdf } from "@/lib/pdf";
import api from "@/lib/api";
import { FileText, Plus, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const ACCENT = "#8B5CF6";

export default function Quotations() {
  const { data: quotes, loading, reload } = useModuleData("/quotations");
  const { items: customers } = usePagedData("/customers", 200);
  const { items: products } = usePagedData("/materials", 200);
  const { sites, activeSite } = useSite();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_code: "", material_code: "", qty: "", unit_price: "", valid_to: "", site_id: "" });

  const create = async () => {
    const cust = customers?.find((c) => c.customer_code === form.customer_code);
    const prod = products?.find((p) => p.material_code === form.material_code);
    try {
      await api.post("/quotations", {
        site_id: form.site_id || (activeSite !== "GROUP" ? activeSite : sites[0]?.id),
        customer_code: form.customer_code, customer_name: cust?.name || form.customer_code,
        valid_to: form.valid_to,
        items: [{ material_code: form.material_code, material_name: prod?.name || form.material_code, qty: Number(form.qty), unit_price: Number(form.unit_price), plant: form.site_id || activeSite }],
      });
      toast.success("Quotation created (EPS-201)");
      setOpen(false); setForm({ customer_code: "", material_code: "", qty: "", unit_price: "", valid_to: "", site_id: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create quotation"); }
  };

  const convert = async (e, q) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/quotations/${q.id}/convert`);
      toast.success(`Converted → Sales Order ${data.order_no}${data.credit_status === "BLOCKED" ? " (credit block!)" : ""}`);
      reload();
      navigate(`/sales/${data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Convert failed"); }
  };

  if (loading && !quotes) return <Loader />;

  const columns = [
    { key: "quote_no", label: "Quotation", render: (r) => <span className="font-mono text-xs text-[#8B5CF6]">{r.quote_no}</span> },
    { key: "customer_name", label: "Customer", render: (r) => (
      <div><div className="text-[#32363a]">{r.customer_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.customer_code}</div></div>) },
    { key: "material_name", label: "Product", render: (r) => <span className="text-[#32363a]">{r.material_name}</span> },
    { key: "net_value", label: "Net Value", render: (r) => <span className="font-mono text-emerald-600">{eur(r.net_value)}</span> },
    { key: "valid_to", label: "Valid To", render: (r) => shortDate(r.valid_to) },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (
      <div className="flex gap-1.5">
        <button data-testid={`quote-pdf-${r.quote_no}`} onClick={(e) => { e.stopPropagation(); openPdf(`/quotations/${r.id}/pdf`); }}
          className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2 py-1 text-[11px] text-[#32363a] hover:border-[#D0313A]"><FileText size={13} /> PDF</button>
        {r.status !== "CONVERTED" && (
          <button data-testid={`quote-convert-${r.quote_no}`} onClick={(e) => convert(e, r)}
            className="inline-flex items-center gap-1 rounded-md bg-[#8B5CF6] px-2 py-1 text-[11px] font-semibold text-white hover:bg-[#7c3aed]"><ArrowRightLeft size={13} /> To Order</button>)}
      </div>) },
  ];

  const inp = "rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm";

  return (
    <div>
      <PageHeader title="Sales Quotations" subtitle="Create customer quotations (EPS-201) and convert accepted quotes into sales orders." icon={FileText} accent={ACCENT}>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-quote-btn" className="inline-flex items-center gap-2 rounded-md bg-[#8B5CF6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7c3aed]"><Plus size={16} /> New Quotation (EPS-201)</button>
          </DialogTrigger>
          <DialogContent className="border-[#e5e9f0] bg-white text-[#32363a]">
            <DialogHeader><DialogTitle className="uppercase">Create Quotation · EPS-201</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <select data-testid="quote-customer" value={form.customer_code} onChange={(e) => setForm({ ...form, customer_code: e.target.value })} className={inp}>
                <option value="">Select customer…</option>{(customers || []).map((c) => <option key={c.customer_code} value={c.customer_code}>{c.name}</option>)}
              </select>
              <select data-testid="quote-material" value={form.material_code} onChange={(e) => setForm({ ...form, material_code: e.target.value })} className={inp}>
                <option value="">Select product…</option>{(products || []).map((p) => <option key={p.material_code} value={p.material_code}>{p.name}</option>)}
              </select>
              {activeSite === "GROUP" && (
                <select data-testid="quote-site" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className={inp}>
                  <option value="">Select site…</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>)}
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="quote-qty" type="number" placeholder="Quantity" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className={inp} />
                <input data-testid="quote-price" type="number" step="0.001" placeholder="Unit price" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className={inp} />
              </div>
              <input data-testid="quote-valid-to" type="date" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} className={inp} />
            </div>
            <DialogFooter>
              <button data-testid="quote-create-submit" onClick={create} disabled={!form.customer_code || !form.material_code || !form.qty} className="rounded-lg bg-[#8B5CF6] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Create Quotation</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <DataTable testId="quotations-table" columns={columns} rows={quotes || []} />
      {(quotes || []).length === 0 && <div className="mt-4 rounded-lg border border-[#e5e9f0] bg-white p-8 text-center text-sm text-[#6a6d70]">No quotations yet — create one with EPS-201.</div>}
    </div>
  );
}
